(() => {
  'use strict';

  if (window.MontkushHomeReviews) {
    window.MontkushHomeReviews.initAll(document);
    return;
  }

  const REQUEST_SIZE = 16;
  const feedCache = new Map();
  const accountCache = new Map();
  const restrictedConditionLanguage = /\b(arthritis|stenosis|anxiety|insomnia|fibromyalgia|tmj|sciatica|migraine|depression|cancer|diabetes|parkinson(?:s)?|epilep\w*|seizure\w*|ptsd|autoimmune|neuropathy)\b/i;
  const restrictedExperienceLanguage = /\b(not sure|no effect|chronic pain|extreme pain|immediate relief|prescription(?: pain)?|works like a miracle|miracle|deep sleep|no more tossing|antidote|shy(?:ness)?|talkative|didn['’]?t|doesn['’]?t|do not|does not)\b/i;
  const text = (value) => value == null ? '' : String(value);
  const numeric = (value, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback;
  const clamp = (value, minimum, maximum) => Math.max(minimum, Math.min(maximum, value));
  const plural = (count, singular, pluralLabel) => `${count} ${count === 1 ? singular : pluralLabel}`;
  const formatCount = (count) => new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(Math.round(numeric(count)));
  const element = (tag, className, value) => { const node = document.createElement(tag); if (className) node.className = className; if (value != null) node.textContent = String(value); return node; };
  const replace = (target, children) => target?.replaceChildren(...children);
  const hidden = (target, value) => { if (target) target.hidden = Boolean(value); };

  const safeUrl = (value) => {
    if (!value || typeof value !== 'string') return '';
    try { const url = new URL(value, window.location.origin); return /^https?:$/.test(url.protocol) ? url.href : ''; } catch (_) { return ''; }
  };
  const inlineText = (value) => text(value).replace(/\s+/g, ' ').trim();
  const trimExcerpt = (value, limit = 230) => {
    const source = inlineText(value);
    if (source.length <= limit) return source;
    const boundary = source.lastIndexOf(' ', limit);
    return `${source.slice(0, boundary > limit * .6 ? boundary : limit).replace(/[\s,;:–—-]+$/, '')}…`;
  };
  const reviewBody = (review) => {
    const content = inlineText(review?.content);
    const title = inlineText(review?.title);
    if (!content) return '';
    if (title && content.toLocaleLowerCase().startsWith(title.toLocaleLowerCase())) {
      const remainder = content.slice(title.length).replace(/^[\s.,:;!?–—-]+/, '').trim();
      if (remainder.length >= 16) return remainder;
    }
    return content;
  };
  const reviewUsefulness = (review) => {
    const length = reviewBody(review).length;
    return { tier: length >= 100 ? 2 : length >= 45 ? 1 : 0, length: Math.min(length, 260) };
  };
  const dateStamp = (review) => { const stamp = new Date(review.createdAt || '').getTime(); return Number.isNaN(stamp) ? 0 : stamp; };
  const dateLabel = (value) => { const date = new Date(value || ''); return Number.isNaN(date.getTime()) ? '' : new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).format(date); };

  const endpoint = (store, product, page = 1) => `https://api-cdn.yotpo.com/v1/widget/${encodeURIComponent(store)}/products/${encodeURIComponent(product)}/reviews.json?per_page=${REQUEST_SIZE}&page=${page}`;
  // Yotpo's native aggregate includes every published product review plus every published site review.
  const accountEndpoint = (store) => `https://api-cdn.yotpo.com/v1/widget/${encodeURIComponent(store)}/reviews.json?per_page=1&page=1&include_site_reviews=true`;
  const sourceFeed = (source, store) => {
    const key = `${store}:${source.id}`;
    if (!feedCache.has(key)) feedCache.set(key, { source, store, reviews: [], bottomline: null, error: '', pending: null });
    return feedCache.get(key);
  };
  const normalise = (raw, source) => ({
    id: text(raw?.id || raw?.review_id),
    score: clamp(numeric(raw?.score || raw?.rating), 0, 5),
    content: text(raw?.content || raw?.review_content || raw?.body).trim(),
    title: text(raw?.title).trim(),
    reviewer: text(raw?.user?.display_name || raw?.user?.name || raw?.display_name || raw?.reviewer_name).trim(),
    verified: Boolean(raw?.verified_buyer || raw?.verified || raw?.is_verified_buyer),
    createdAt: raw?.created_at || raw?.created_at_date || raw?.date || raw?.updated_at || '',
    date: dateLabel(raw?.created_at || raw?.created_at_date || raw?.date || raw?.updated_at),
    source,
    raw
  });
  const load = (feed) => {
    if (feed.pending) return feed.pending;
    feed.pending = fetch(endpoint(feed.store, feed.source.id), { headers: { Accept: 'application/json' }, credentials: 'omit' })
      .then(async (response) => {
        if (!response.ok) throw new Error(`The reviews service returned ${response.status}.`);
        const payload = (await response.json())?.response;
        if (!payload || !Array.isArray(payload.reviews)) throw new Error('The reviews service returned an unexpected response.');
        feed.reviews = payload.reviews.map((review) => normalise(review, feed.source)).filter((review) => review.content);
        feed.bottomline = payload.bottomline || null;
        feed.error = '';
        return feed;
      })
      .catch((error) => { feed.error = error instanceof Error ? error.message : 'Reviews could not be loaded.'; return feed; })
      .finally(() => { feed.pending = null; });
    return feed.pending;
  };

  const loadAccountSummary = (store) => {
    if (!accountCache.has(store)) {
      accountCache.set(store, fetch(accountEndpoint(store), { headers: { Accept: 'application/json' }, credentials: 'omit' })
        .then(async (response) => {
          if (!response.ok) throw new Error(`The account reviews service returned ${response.status}.`);
          const payload = (await response.json())?.response;
          const bottomline = payload?.bottomline || {};
          const total = numeric(bottomline.total_review ?? bottomline.total_reviews ?? payload?.pagination?.total);
          const average = numeric(bottomline.average_score);
          if (total < 1 || average <= 0) throw new Error('The account reviews service returned an incomplete total.');
          return { total, average };
        })
        .catch(() => null));
    }
    return accountCache.get(store);
  };

  const stars = (score) => {
    const value = clamp(numeric(score), 0, 5);
    const root = element('span', 'mk-home-reviews__stars');
    root.setAttribute('role', 'img'); root.setAttribute('aria-label', `${value.toFixed(1)} out of 5 stars`);
    const base = element('span', 'mk-home-reviews__stars-base', '★★★★★'); base.setAttribute('aria-hidden', 'true');
    const fill = element('span', 'mk-home-reviews__stars-fill', '★★★★★'); fill.setAttribute('aria-hidden', 'true'); fill.style.width = `${value / 5 * 100}%`;
    root.append(base, fill); return root;
  };
  const skeletons = () => Array.from({ length: 3 }, () => element('div', 'mk-home-reviews__skeleton'));

  const reviewDialog = (root) => {
    const dialog = element('dialog', 'mk-home-reviews__dialog');
    const panel = element('div', 'mk-home-reviews__dialog-panel');
    const close = element('button', 'mk-home-reviews__dialog-close', '×'); close.type = 'button'; close.setAttribute('aria-label', 'Close full review');
    const content = element('div', 'mk-home-reviews__dialog-content');
    panel.append(close, content); dialog.append(panel); root.append(dialog);
    let trigger = null;
    const closeDialog = () => typeof dialog.close === 'function' ? dialog.close() : dialog.removeAttribute('open');
    close.addEventListener('click', closeDialog);
    dialog.addEventListener('click', (event) => { if (event.target === dialog) closeDialog(); });
    dialog.addEventListener('close', () => trigger?.focus());
    return {
      open(review, from) {
        trigger = from;
        const product = document.createElement('a'); product.className = 'mk-home-reviews__dialog-product'; product.href = review.source.url; product.textContent = `Shop ${review.source.label} →`;
        const quote = element('blockquote', 'mk-home-reviews__dialog-quote', review.content);
        const meta = element('div', 'mk-home-reviews__dialog-meta');
        if (review.reviewer) meta.append(element('strong', 'mk-home-reviews__reviewer', review.reviewer));
        if (review.verified) meta.append(element('span', 'mk-home-reviews__verified', 'Verified buyer'));
        if (review.date) meta.append(element('span', 'mk-home-reviews__date', review.date));
        replace(content, [product, element('div', 'mk-home-reviews__dialog-stars')].map((node, index) => { if (index === 1) node.append(stars(review.score)); return node; }).concat([quote, meta]));
        if (typeof dialog.showModal === 'function') dialog.showModal(); else dialog.setAttribute('open', '');
        close.focus();
      }
    };
  };

  const card = (review, dialog) => {
    const article = element('article', 'mk-home-reviews__card');
    const top = element('div', 'mk-home-reviews__card-top');
    const product = document.createElement('a'); product.className = 'mk-home-reviews__product'; product.href = review.source.url; product.textContent = review.source.label; product.setAttribute('aria-label', `Shop ${review.source.title}`);
    top.append(product, stars(review.score));
    const copy = element('div', 'mk-home-reviews__copy');
    const title = inlineText(review.title) || trimExcerpt(reviewBody(review), 110);
    const excerpt = trimExcerpt(reviewBody(review));
    if (title) copy.append(element('h3', 'mk-home-reviews__review-title', title));
    if (excerpt && excerpt.toLocaleLowerCase() !== title.toLocaleLowerCase()) copy.append(element('p', 'mk-home-reviews__review-excerpt', excerpt));
    const bottom = element('div', 'mk-home-reviews__bottom');
    if (review.reviewer) bottom.append(element('span', 'mk-home-reviews__reviewer', review.reviewer));
    if (review.verified) bottom.append(element('span', 'mk-home-reviews__verified', 'Verified buyer'));
    if (review.date) bottom.append(element('time', 'mk-home-reviews__date', review.date));
    const more = element('button', 'mk-home-reviews__read-more', 'Read full review'); more.type = 'button'; more.setAttribute('aria-label', `Read full review by ${review.reviewer || 'customer'}`); more.addEventListener('click', () => dialog.open(review, more));
    article.append(top, copy, bottom, more);
    return article;
  };

  const init = (root) => {
    if (!root || root.dataset.mkHomeReviewsInitialised === 'true') return;
    root.dataset.mkHomeReviewsInitialised = 'true';
    const store = text(root.dataset.yotpoStore).trim();
    const limit = clamp(numeric(root.dataset.cardLimit, 6), 3, 9);
    const sources = [...root.querySelectorAll('[data-mk-home-review-source]')].map((node) => ({
      id: text(node.dataset.productId).trim(), title: text(node.dataset.productTitle).trim(), label: text(node.dataset.productLabel).trim(), url: safeUrl(node.dataset.productUrl)
    })).filter((source) => source.id && source.url);
    const cards = root.querySelector('[data-mk-home-reviews-cards]');
    const status = root.querySelector('[data-mk-home-reviews-status]');
    const summary = root.querySelector('[data-mk-home-reviews-summary]');
    const heading = root.querySelector('[data-mk-home-reviews-heading]');
    const headingLabel = text(heading?.dataset.reviewLabel || heading?.textContent).replace(/^\s*[\d.,]+(?:k|m)?\+?\s*/i, '').trim() || 'verified customer reviews';
    const error = root.querySelector('[data-mk-home-reviews-error]');
    const pagination = root.querySelector('[data-mk-home-reviews-pagination]');
    const controls = root.querySelector('[data-mk-home-reviews-controls]');
    const previous = root.querySelector('[data-mk-home-reviews-previous]');
    const next = root.querySelector('[data-mk-home-reviews-next]');
    if (!store || !sources.length || !cards) { if (status) status.textContent = 'Customer reviews are not configured yet.'; return; }

    const dialog = reviewDialog(root);
    const render = (feeds, accountSummary) => {
      const sortFeatured = (a, b) => {
        const aUseful = reviewUsefulness(a); const bUseful = reviewUsefulness(b);
        return b.score - a.score || bUseful.tier - aUseful.tier || bUseful.length - aUseful.length || dateStamp(b) - dateStamp(a);
      };
      const candidates = feeds.map((feed) => feed.reviews
        .filter((review) => review.score >= 4 && !restrictedConditionLanguage.test(`${review.title} ${review.content}`) && !restrictedExperienceLanguage.test(`${review.title} ${review.content}`))
        .sort(sortFeatured));
      const reviews = [];
      const used = new Set();
      const cursors = candidates.map(() => 0);
      while (reviews.length < limit) {
        let added = false;
        candidates.forEach((sourceReviews, sourceIndex) => {
          while (cursors[sourceIndex] < sourceReviews.length && used.has(sourceReviews[cursors[sourceIndex]].id)) cursors[sourceIndex] += 1;
          const review = sourceReviews[cursors[sourceIndex]];
          if (review && reviews.length < limit) {
            reviews.push(review);
            used.add(review.id);
            cursors[sourceIndex] += 1;
            added = true;
          }
        });
        if (!added) break;
      }
      reviews.length = Math.min(reviews.length, limit);
      const failures = feeds.filter((feed) => feed.error).length;
      const bottomlines = feeds.map((feed) => feed.bottomline).filter(Boolean);
      const totals = bottomlines.reduce((sum, line) => sum + numeric(line.total_review), 0);
      const weighted = bottomlines.reduce((sum, line) => sum + numeric(line.average_score) * numeric(line.total_review), 0);
      const accountTotal = numeric(accountSummary?.total);
      const accountAverage = numeric(accountSummary?.average);
      if (heading && accountTotal > 0) heading.textContent = `${formatCount(accountTotal)} ${headingLabel}`;
      if (summary) {
        if (accountTotal > 0 && accountAverage > 0) {
          summary.hidden = false;
          replace(summary, [stars(accountAverage), element('span', '', `${accountAverage.toFixed(1)} / 5 from ${formatCount(accountTotal)} ${accountTotal === 1 ? 'published review' : 'published reviews'}`)]);
        } else if (totals > 0 && weighted > 0) {
          summary.hidden = false;
          replace(summary, [stars(weighted / totals), element('span', '', `${(weighted / totals).toFixed(1)} / 5 across featured products`)]);
        } else {
          summary.hidden = true;
          replace(summary, []);
        }
      }
      if (!reviews.length) {
        replace(cards, []); hidden(error, failures === 0); if (status) status.textContent = failures ? 'Reviews are temporarily unavailable.' : 'No published reviews are available yet.'; return;
      }
      replace(cards, reviews.map((review) => card(review, dialog)));
      hidden(error, true);
      if (status) status.textContent = `${plural(reviews.length, 'published review', 'published reviews')} are shown from the MONTKUSH community.`;
      setupNavigation(reviews.length);
    };
    const setupNavigation = (count) => {
      if (!pagination || !controls) return;
      const dots = Array.from({ length: count }, (_, index) => { const dot = element('button', 'mk-home-reviews__dot'); dot.type = 'button'; dot.setAttribute('aria-label', `Show review ${index + 1} of ${count}`); dot.addEventListener('click', () => moveTo(index)); return dot; });
      replace(pagination, dots); hidden(controls, count <= 1);
      const update = () => {
        const cardNodes = [...cards.children]; if (!cardNodes.length) return;
        const midpoint = cards.scrollLeft + cards.clientWidth / 2;
        const active = cardNodes.reduce((winner, item, index) => Math.abs(item.offsetLeft + item.offsetWidth / 2 - midpoint) < Math.abs(cardNodes[winner].offsetLeft + cardNodes[winner].offsetWidth / 2 - midpoint) ? index : winner, 0);
        dots.forEach((dot, index) => { dot.classList.toggle('is-active', index === active); if (index === active) dot.setAttribute('aria-current', 'true'); else dot.removeAttribute('aria-current'); });
        if (previous) previous.disabled = active === 0;
        if (next) next.disabled = active === cardNodes.length - 1;
      };
      const horizontalInset = () => Number.parseFloat(getComputedStyle(cards).scrollPaddingLeft) || Number.parseFloat(getComputedStyle(cards).paddingLeft) || 0;
      const moveTo = (index) => {
        const item = cards.children[index];
        if (item) cards.scrollTo({ left: Math.max(0, item.offsetLeft - horizontalInset()), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      };
      previous?.addEventListener('click', () => { const active = Math.round(cards.scrollLeft / Math.max(1, cards.children[0]?.offsetWidth + 20)); moveTo(Math.max(0, active - 1)); });
      next?.addEventListener('click', () => { const active = Math.round(cards.scrollLeft / Math.max(1, cards.children[0]?.offsetWidth + 20)); moveTo(Math.min(count - 1, active + 1)); });
      cards.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
      requestAnimationFrame(update);
    };
    const loadReviews = async () => {
      cards.setAttribute('aria-busy', 'true'); replace(cards, skeletons()); if (status) status.textContent = 'Loading published customer reviews…';
      const [feeds, accountSummary] = await Promise.all([Promise.all(sources.map((source) => load(sourceFeed(source, store)))), loadAccountSummary(store)]);
      cards.setAttribute('aria-busy', 'false'); render(feeds, accountSummary);
    };
    const observe = new IntersectionObserver((entries) => { if (entries.some((entry) => entry.isIntersecting)) { observe.disconnect(); loadReviews(); } }, { rootMargin: '300px 0px' });
    observe.observe(root);
  };

  const initAll = (scope = document) => { const roots = []; if (scope instanceof Element && scope.matches('[data-mk-home-reviews]')) roots.push(scope); if (scope.querySelectorAll) roots.push(...scope.querySelectorAll('[data-mk-home-reviews]')); roots.forEach(init); };
  window.MontkushHomeReviews = { initAll, endpoint };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true }); else initAll(document);
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
